import { useEffect, useRef, useState } from "react";
import { Dialog, FormControl, Select, Text } from "@primer/react";
import { AlertIcon, CheckCircleIcon, SyncIcon, TrashIcon } from "@primer/octicons-react";
import { Alert } from "../components/Alert/Alert.jsx";
import { Button } from "../components/Button/Button.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { Section } from "../components/Section/Section.jsx";
import { ThemePicker } from "../components/ThemePicker/ThemePicker.jsx";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import {
  chooseBackupDirectory, chooseRestoreBackupFile, clearBackupDirectory, getBackupSettings,
  parseBackupPayload, setBackupInterval, writeBackup
} from "../storage/backupService.js";
import "./SettingsPage.css";

const intervalLabels = { "15m": "Every 15 minutes", "1h": "Every hour", "4h": "Every 4 hours", "1d": "Daily" };

export function SettingsPage({ onApplicationReset }) {
  const application = useApplicationDocument();
  const [settings, setSettings] = useState({ handle: null, interval: "1h", lastBackupAt: null });
  const [backupStatus, setBackupStatus] = useState(null);
  const [restoreStatus, setRestoreStatus] = useState(null);
  const [restore, setRestore] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => { getBackupSettings().then(setSettings).catch((error) => setBackupStatus({ tone: "danger", text: error.message })); }, []);

  async function selectFolder() {
    try {
      const handle = await chooseBackupDirectory();
      setSettings((current) => ({ ...current, handle }));
      await backupNow();
    } catch (error) {
      if (error?.name !== "AbortError") setBackupStatus({ tone: "danger", text: error.message });
    }
  }

  async function backupNow() {
    if (!application.applicationDocument) return;
    setBackupStatus({ text: "Writing backup…" });
    try {
      const result = await writeBackup(application.applicationDocument);
      if (result.status === "missing") return selectFolder();
      if (result.status === "denied") throw new Error("Regulator needs permission to write to the backup folder.");
      setSettings((current) => ({ ...current, lastBackupAt: result.savedAt }));
      setBackupStatus({ tone: "success", text: "Backup saved." });
    } catch (error) {
      setBackupStatus({ tone: "danger", text: error.message });
    }
  }

  async function disableBackup() {
    await clearBackupDirectory();
    setSettings((current) => ({ ...current, handle: null, lastBackupAt: null }));
    setBackupStatus({ text: "Automatic backup disabled." });
  }

  async function changeInterval(event) {
    const interval = event.target.value;
    await setBackupInterval(interval);
    setSettings((current) => ({ ...current, interval }));
    setBackupStatus({ tone: "success", text: `Automatic backup set to ${intervalLabels[interval].toLowerCase()}.` });
  }

  async function readRestoreFile(file) {
    if (!file) return;
    try {
      const payload = JSON.parse(await file.text());
      const document = parseBackupPayload(payload);
      setRestore({ document, fileName: file.name, profileCount: document.profiles.length });
      setRestoreStatus(null);
    } catch (error) {
      setRestore(null);
      setRestoreStatus({ tone: "danger", text: `Could not read backup: ${error.message}` });
    }
  }

  async function chooseRestoreFile() {
    if (typeof window.showOpenFilePicker !== "function") {
      fileInput.current?.click();
      return;
    }
    try {
      const file = await chooseRestoreBackupFile(settings.handle ?? "downloads");
      await readRestoreFile(file);
    } catch (error) {
      if (error?.name !== "AbortError") {
        setRestoreStatus({ tone: "danger", text: `Could not open backup: ${error.message}` });
      }
    }
  }

  async function confirmRestore() {
    const restored = await application.restoreApplicationDocument(restore.document);
    if (restored) {
      setRestoreStatus({ tone: "success", text: `Restored ${restore.fileName}.` });
      setRestore(null);
    }
  }

  async function resetApplication() {
    if (await application.resetApplicationDocument()) {
      setResetOpen(false);
      onApplicationReset?.();
    }
  }

  if (application.error && !application.applicationDocument) return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;

  return <article className="settings-page narrow-page">
    <PageHeading>Settings</PageHeading>
    <Section heading="Appearance"><div className="settings-preferences"><ThemePicker /></div></Section>
    <Section heading="Backup" description="Automatic backups run after changes while Regulator is open.">
      <div className="backup-settings">
        <div className="backup-fields">
          <FormControl><FormControl.Label>Backup folder</FormControl.Label><div className="field-control"><Button tone="secondary" onClick={selectFolder}>{settings.handle ? "Change folder" : "Select folder"}</Button></div><FormControl.Caption>{settings.handle?.name ?? "No folder selected"}</FormControl.Caption></FormControl>
          <FormControl><FormControl.Label>Schedule</FormControl.Label><div className="field-control"><Select value={settings.interval} onChange={changeInterval} disabled={!settings.handle}>{Object.entries(intervalLabels).map(([value, label]) => <Select.Option key={value} value={value}>{label}</Select.Option>)}</Select>{settings.handle ? <Button tone="quiet" onClick={disableBackup}>Disable</Button> : null}</div></FormControl>
        </div>
        <div className="backup-action"><Button onClick={backupNow} disabled={!application.applicationDocument}>Backup Now</Button>{backupStatus ? <SettingsStatus status={backupStatus} /> : settings.lastBackupAt ? <SettingsStatus status={{ text: `Last backup ${new Date(settings.lastBackupAt).toLocaleString()}` }} /> : null}</div>
      </div>
    </Section>
    <Section heading="Restore" description="Replaces all current profiles and the active-profile selection.">
      <div className="restore-settings"><Text className="settings-help">Choose a Regulator JSON backup. It is validated before any data changes.</Text><input ref={fileInput} type="file" accept=".json,application/json" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; readRestoreFile(file); }} hidden /><div><Button tone="secondary" onClick={chooseRestoreFile}>Choose backup file</Button></div>{restoreStatus ? <SettingsStatus status={restoreStatus} /> : null}{restore ? <div className="restore-confirmation"><Alert tone="warning" heading={`Replace current data with “${restore.fileName}”?`}>This backup contains {restore.profileCount} {restore.profileCount === 1 ? "profile" : "profiles"}. This cannot be undone unless you create a backup first.</Alert><div><Button tone="quiet" onClick={() => setRestore(null)}>Cancel</Button><Button tone="danger" onClick={confirmRestore} disabled={application.saving}>{application.saving ? "Restoring…" : "Restore and replace"}</Button></div></div> : null}</div>
    </Section>
    <Section heading="Danger zone" description="Destructive actions affect every profile.">
      <div className="danger-zone">
        <div><strong>Delete all data</strong><Text>Delete every profile and return to a blank Master profile. Appearance and backup settings are kept.</Text></div>
        <Button tone="danger" leadingVisual={TrashIcon} onClick={() => setResetOpen(true)}>Delete all data</Button>
      </div>
    </Section>
    {resetOpen ? <Dialog className="reset-data-dialog" title="Delete all data?" subtitle={`This will permanently delete ${application.profiles.length} ${application.profiles.length === 1 ? "profile" : "profiles"}.`} width="medium" onClose={() => setResetOpen(false)} footerButtons={[{ content: "Cancel", onClick: () => setResetOpen(false) }, { content: application.saving ? "Deleting…" : "Delete all data", buttonType: "danger", onClick: resetApplication, disabled: application.saving }]}>
      <Alert tone="warning" heading="This cannot be undone">A blank Master profile will be created. Download a backup first if you may need this data again.</Alert>
    </Dialog> : null}
  </article>;
}

function SettingsStatus({ status }) {
  const Icon = status.tone === "success" ? CheckCircleIcon : status.tone === "danger" ? AlertIcon : SyncIcon;
  return <Text role={status.tone === "danger" ? "alert" : "status"} className={`settings-status ${status.tone ?? ""}`}><Icon aria-hidden="true" /><span>{status.text}</span></Text>;
}
